import React, { useMemo } from 'react';
import type { IOfferCardHeaderProps } from './types/offerCardHeaderProps';
import { Typography } from '@interstate/components/Typography';
import { getHeaderLabel, getApplicationOrSourceName, personaSuffix } from '../utils/getHeaderLabel';
import { VERSION_CREATED_BY_TO_APP_DISPLAY_NAME } from '../constants';

// Source types
const SourceType = {
    SALESPERSON: 'sales',
    DEALMAKER: 'dealmaker'
};

export const OfferCardHeader: React.FC<IOfferCardHeaderProps> = ({
    shopperFirstName,
    shopperLastName,
    createdOnDate,
    createdOnTime,
    offerSource,
    applicationName,
    isDefaultOffer,
    isMostRecentDealerOffer,
    isMostRecentShopperOffer,
    showSubHeader = true,
    employees
}: IOfferCardHeaderProps) => {
    const sentBy = useMemo(() => {
        if (applicationName) {
            const appName = VERSION_CREATED_BY_TO_APP_DISPLAY_NAME[applicationName];
            if (appName) {
                return appName;
            }
        }
        return offerSource?.toLowerCase() === SourceType.DEALMAKER ? 'You' : `${shopperFirstName} ${shopperLastName}`;
    }, [applicationName, offerSource, shopperFirstName, shopperLastName]);

    // Compute sales data
    const salesOfferData = useMemo(() => {
        // Get the source type and check if it's a sales source
        const sourceType = getApplicationOrSourceName(offerSource, applicationName, personaSuffix);
        const isSalesSource = sourceType?.toLowerCase() === SourceType.SALESPERSON;

        // Check if we should display employee details
        const shouldShowSVEmployeeDetails = Boolean(employees?.length);

        // Early return if it's not a sales source or employees is empty
        if (!isSalesSource || !shouldShowSVEmployeeDetails) {
            return {
                hasSalesEmployeeData: false,
                isSalesSource,
                shouldShowSVEmployeeDetails: !isSalesSource ? false : shouldShowSVEmployeeDetails,
                salesEmployeeDisplayName: ''
            };
        }

        // Format employee display name
        let salesEmployeeDisplayName = '';

        const employee = employees![0];

        if (employee) {
            const { firstName = '', lastName = '', salutation = '' } = employee;

            // Only create a name if first or last name exists
            if (firstName || lastName) {
                const employeeFullName = `${firstName} ${lastName}`.trim();
                salesEmployeeDisplayName = salutation ? `${salutation} ${employeeFullName}` : employeeFullName;
            }
        }

        return {
            hasSalesEmployeeData: true,
            isSalesSource,
            shouldShowSVEmployeeDetails,
            salesEmployeeDisplayName
        };
    }, [employees, offerSource, applicationName]);

    const getSubHeaderText = () => {
        if (salesOfferData.isSalesSource) {
            // If it's a sales source but has no employee name, return Not Available in place of employees name
            if (!salesOfferData.salesEmployeeDisplayName) {
                return `Sent By: Not Available on ${createdOnDate} at ${createdOnTime}`;
            }
            // Otherwise return the full details with employee name
            return `Sent By: ${salesOfferData.salesEmployeeDisplayName} on ${createdOnDate} at ${createdOnTime}`;
        }
        return `Sent By: ${sentBy} on ${createdOnDate} at ${createdOnTime}`;
    };

    if (!createdOnDate || !createdOnTime) {
        return null;
    }

    if (isDefaultOffer) {
        return (
            <Typography variant="h4" tag="span">
                Default Offer
            </Typography>
        );
    } else {
        return (
            <>
                <Typography variant="h4" data-testid="most-recent">
                    {getHeaderLabel(isMostRecentDealerOffer, isMostRecentShopperOffer, offerSource, applicationName)}
                </Typography>
                {showSubHeader && (
                    <Typography variant="body-sm" tag="span">
                        {getSubHeaderText()}
                    </Typography>
                )}
            </>
        );
    }
};

export default OfferCardHeader;
